import { useState, useEffect } from "react";
import { FaPhoneAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import {
  Box,
  Heading,
  Text,
  Button,
  Link,
  ButtonGroup,
  Container,
  useBreakpointValue,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";

import vid from "../../vid/Hero2.mp4";

const MotionBox = motion(Box);

export default function Hero() {
  const mainColor = "#fff";
  const accentColor = "#c6a664";
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const header = t("header", { returnObjects: true });
  const contact = t("contact", { returnObjects: true });
  const phoneNumber = contact?.phone?.replace(/\s/g, "") || "+966112139740";
  const phoneHref = `tel:${phoneNumber}`;

  const slides = [
    {
      title: isArabic ? "شركة الفا المتحدة للاستثمار" : "Alpha United Investment",
      subtitle: isArabic
        ? "قيادة النمو من خلال استثمارات استراتيجية ومتنوعة"
        : "Driving growth through strategic and diversified investments",
    },
    {
      title: isArabic ? "تنويع الاستثمارات" : "Diversifying Investments",
      subtitle: isArabic
        ? "نركز على فرص النمو في قطاعات العقارات، التقنية، والرعاية الصحية"
        : "Focusing on growth opportunities in real estate, technology, and healthcare sectors",
    },
    {
      title: isArabic ? "قيمنا أساس نجاحنا" : "Our Values Drive Our Success",
      subtitle: isArabic
        ? "نؤمن بالاستدامة، والابتكار، والشراكة طويلة الأمد مع شركائنا"
        : "We believe in sustainability, innovation, and long-term partnerships",
    },
  ];

  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  useEffect(() => {
    document.documentElement.dir = isArabic ? "rtl" : "ltr";
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <Box
      id="hero"
      position="relative"
      w="100%"
      h={{ base: "80vh", md: "100vh" }}
      overflow="hidden"
      color="white"
    >
      {/* Background Video */}
      <Box
        as="video"
        autoPlay
        loop
        muted
        playsInline
        position="absolute"
        top="50%"
        left="50%"
        w="100%"
        h="100%"
        objectFit="cover"
        transform="translate(-50%, -50%)"
        zIndex={0}
      >
        <source src={vid} type="video/mp4" />
      </Box>

      {/* Overlay for readability */}
      <Box
        position="absolute"
        top={0}
        left={0}
        w="100%"
        h="100%"
        bgGradient="linear(to-b, rgba(0,0,0,0.2), rgba(0,0,0,0.3))"
        zIndex={1}
      />

      {/* Text Carousel */}
      <Container
        maxW="6xl"
        h="100%"
        position="relative"
        zIndex={2}
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
        mt={"100"}
      >
        <AnimatePresence mode="wait" initial={false}>
          <MotionBox
            key={current}
            maxW={{ base: "90%", md: "70%" }}
            position="absolute"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            textAlign="center"
          >
            <Heading
              fontSize={{ base: "2xl", sm: "3xl", md: "4xl", lg: "5xl" }}
              fontWeight="bold"
              mb={4}
              color={mainColor}
            >
              {slides[current].title}
            </Heading>

            <Text
              fontSize={{ base: "md", sm: "lg", md: "xl" }}
              color="gray.200"
              mb={8}
            >
              {slides[current].subtitle}
            </Text>

            <ButtonGroup justifyContent="center">
              <Button
                leftIcon={<FaPhoneAlt />}
                colorScheme="whiteAlpha"
                variant="outline"
                size={useBreakpointValue({ base: "md", md: "lg" })}
                borderColor={accentColor}
                color={accentColor}
                _hover={{
                  bg: accentColor,
                  color: "black",
                  borderColor: accentColor,
                }}
              >
                <Link href={phoneHref} _hover={{ textDecoration: "none" }}>
                  {header?.cta || (isArabic ? "اتصل بنا" : "Contact Us")}
                </Link>
              </Button>
            </ButtonGroup>
          </MotionBox>
        </AnimatePresence>
      </Container>
    </Box>
  );
}
